import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

import { useTheme } from '../../../../contexts/ThemeContext';
import { useConversation } from '../../../../contexts/ConversationContext';
import { usePersonaName, usePersonaColor } from '../../../../hooks/usePersonaColor';
import { getStanceToken } from '../chatDagTokens';
import { promoteDivergence } from '../../../../services/chorusApi';
import { StanceChip } from './StanceChip';
import SpineConversationBubble from './SpineConversationBubble';
import { CostDivergenceBadge } from '../cost/CostDivergenceBadge';
import { useOvershootMap } from '../cost/useOvershootMap';
import type { PeerResponseData } from '../types';

// SP-3: stance badge + border-style encoding. The left border stays as a
// solid 3px persona-accent stripe (intentional asymmetry — triad fix #3).
// Stance border-style applies to the other three sides via className.
const PeerResponseNode: React.FC<NodeProps<PeerResponseData>> = ({ data }) => {
  const { darkMode } = useTheme();
  const sessionId = useConversation().state?.id ?? null;   // TH-4 promote wire
  const personaName = usePersonaName(data.personaId);
  const color = usePersonaColor({
    id: data.personaId,
    accent_color: undefined,
    name: personaName,
  });
  const stance = getStanceToken(data.stance);
  const hasStance = data.stance && data.stance !== 'neutral';

  const modelShort = (data.modelUsed ?? '')
    .split('/').pop()?.split('-').slice(0, 2).join('-');

  // G4b — overshoot membership by this node's turn message id (presentational flip).
  const overshoot = useOvershootMap().get(data.messageId ?? '');

  // Source Spines SP-2 — blueprint-spine replies render as the rich
  // SpineConversationBubble (stamped by stampSpineContext). Squad peer_responses
  // (no spineSpawnSource) keep the compact chip below. Handles preserved so the
  // spine edges still connect (laneIndex drives the entry cascade; Task 4 stamps it).
  if (data.spineSpawnSource === 'blueprint_intent') {
    return (
      <>
        <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0 }} />
        <SpineConversationBubble data={data} index={data.laneIndex ?? 0} />
        <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0 }} />
      </>
    );
  }

  return (
    <div
      className={`nodrag rounded-md border px-2.5 py-2 text-[11px] select-none ${
        hasStance
          ? stance.borderClass
          : darkMode
            ? 'border-gray-600/80'
            : 'border-gray-300'
      } ${darkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}
      style={{ width: 280, borderLeftColor: color.hex, borderLeftWidth: 3 }}
      data-testid={`peer-response-${data.personaId}`}
    >
      <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0 }} />
      <div className="flex items-center gap-1.5 mb-1">
        <span
          aria-hidden
          style={{ width: 8, height: 8, borderRadius: '50%', background: color.hex }}
        />
        <span className="font-medium text-[10px] truncate" style={{ flex: 1 }}>{personaName}</span>
        {data.stance && (
          <span
            className={`text-[11px] px-1 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
            title={stance.label}
          >
            {stance.glyph} {stance.label}
          </span>
        )}
        {modelShort && (
          <span className={`text-[9px] px-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            {modelShort}
          </span>
        )}
        <CostDivergenceBadge telemetry={data.costTelemetry} overshoot={overshoot} />
      </div>
      <div className="text-[11px] leading-tight line-clamp-3">{data.preview}</div>
      {data.resolvedTarget && (
        <div
          data-testid={`peer-target-${data.personaId}`}
          title={data.resolvedTarget.source === 'persona'
            ? `Own target: ${data.resolvedTarget.title}`
            : `Inherited from ${data.resolvedTarget.source}: ${data.resolvedTarget.title}`}
          className={`mt-1 flex items-center gap-1 text-[9px] truncate ${
            data.resolvedTarget.source === 'persona'
              ? (darkMode ? 'text-indigo-300' : 'text-indigo-600')
              : (darkMode ? 'text-gray-500 italic' : 'text-gray-400 italic')
          }`}
        >
          <span aria-hidden>⛰</span>
          {data.resolvedTarget.source !== 'persona' && (
            <span className="sr-only">inherited from {data.resolvedTarget.source}: </span>
          )}
          <span className="truncate">
            <span aria-hidden>{data.resolvedTarget.source !== 'persona' && '↑ '}</span>
            {data.resolvedTarget.title}
          </span>
        </div>
      )}
      {data.chorusStance && (
        <div className="mt-1">
          <StanceChip
            stance={data.chorusStance}
            onPromote={() => {
              if (sessionId && data.messageId) void promoteDivergence(sessionId, data.messageId);
            }}
          />
        </div>
      )}
      <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

export default PeerResponseNode;
